// Gate 5.2 · Publish Calendar
//
// Spatial projection over `bos_publish_schedules`. Drag any pending
// schedule onto a different day to reschedule (hour-of-day is preserved
// in the org timezone). Terminal rows render as read-only pills with a
// text status ("Sent" / "Failed" / "Paused" / "Scheduled") in addition
// to the status color — Roger's acceptance review required text so the
// state is never color-only.
//
// Mobile (< 768px) renders an agenda view: a single column of day
// headings with chips beneath, so Fri/Sat never clip off the right
// edge the way the grid did on his phone.
//
// All times — chip, header, agenda, flash — are formatted in the org
// timezone via `lib/tz.js`. The datetime shown on each chip includes
// the EST/EDT abbreviation so the user never has to guess.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, LoadingCard, Pill } from "../lib/ui";
import {
  channelDisplay,
  dateKeyInTz,
  fmtChipTime,
  fmtDateTimeTz,
  fmtWeekdayDateTz,
  parseIsoAsUtc,
  partsInTz,
  parseDatetimeLocalInTz,
  useOrgTimezone,
} from "../lib/tz";

/* -------------------------------------------------------------------------- */
/*  Status rendering (color + text, both)                                     */
/* -------------------------------------------------------------------------- */

// Effective status comes from the schedule state, but the server
// reconciles state <-> receipt execution_status in list_schedules, so
// the color on this chip always matches the receipt outcome.
const STATE_TONE = {
  scheduled:                   "muted",
  executing:                   "warn",
  executed:                    "success",
  failed:                      "danger",
  canceled:                    "muted",
  paused_by_operator:          "warn",
  paused_unhealthy_connection: "danger",
  missed:                      "danger",
};

// Short text label that must appear next to the color — never
// color-only.
const STATE_LABEL = {
  scheduled:                   "Scheduled",
  executing:                   "Sending",
  executed:                    "Sent",
  failed:                      "Failed",
  canceled:                    "Canceled",
  paused_by_operator:          "Paused",
  paused_unhealthy_connection: "Paused",
  missed:                      "Missed",
};

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/* -------------------------------------------------------------------------- */
/*  Date helpers (naive — we only use these to build grid cells)              */
/* -------------------------------------------------------------------------- */

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999); }
function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }

function isoDayKey(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear()
      && a.getMonth() === b.getMonth()
      && a.getDate() === b.getDate();
}

function buildMonthGrid(anchor) {
  const first = startOfMonth(anchor);
  const startWeekday = first.getDay();
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - startWeekday);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push(d);
  }
  return cells;
}

/* -------------------------------------------------------------------------- */
/*  Chip                                                                      */
/* -------------------------------------------------------------------------- */

function ScheduleChip({ schedule, onDragStart, terminal, tz }) {
  const draggable = !terminal;
  const label = STATE_LABEL[schedule.state] || schedule.state;
  const chName = channelDisplay(schedule.channel);
  return (
    <div
      className="bo-cal-chip"
      draggable={draggable}
      onDragStart={draggable ? (e) => onDragStart(e, schedule) : undefined}
      data-testid={`cal-chip-${schedule.id}`}
      style={{
        cursor:  draggable ? "grab" : "default",
        opacity: terminal ? 0.72 : 1,
      }}
      title={`${chName} · ${label} · ${fmtDateTimeTz(schedule.scheduled_for, tz)}`}
    >
      <span
        className="bo-cal-chip-time"
        data-testid={`cal-chip-time-${schedule.id}`}
      >
        {fmtChipTime(schedule.scheduled_for, tz)}
      </span>
      <Pill
        tone={STATE_TONE[schedule.state] || "muted"}
        testId={`cal-chip-state-${schedule.id}`}
      >
        {chName}
      </Pill>
      <span
        data-testid={`cal-chip-status-${schedule.id}`}
        className="bo-cal-chip-status"
        style={{
          fontSize: 11,
          marginLeft: 4,
          color:
            schedule.state === "failed"             ? "var(--bo-danger)"
            : schedule.state === "executed"         ? "var(--bo-success)"
            : schedule.state === "paused_by_operator" ? "var(--bo-warn, #a05a00)"
            : "var(--bo-muted)",
          fontWeight: 600,
        }}
      >
        {label}
      </span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Agenda row — mobile layout                                                */
/* -------------------------------------------------------------------------- */

function AgendaDay({ dayKey, dayChips, tz, isToday, dateObj }) {
  return (
    <div
      className={`bo-cal-agenda-day ${isToday ? "bo-cal-agenda-day-today" : ""}`}
      data-testid={`cal-agenda-day-${dayKey}`}
    >
      <div className="bo-cal-agenda-day-header">
        <span className="bo-cal-agenda-day-label">
          {fmtWeekdayDateTz(dateObj.toISOString(), tz)}
        </span>
        {isToday && (
          <span className="bo-cal-today-badge" data-testid={`cal-agenda-today-${dayKey}`}>
            Today
          </span>
        )}
      </div>
      {dayChips.length === 0 ? (
        <div
          className="bo-cal-agenda-empty"
          data-testid={`cal-agenda-empty-${dayKey}`}
        >
          Nothing scheduled
        </div>
      ) : (
        <div className="bo-cal-agenda-body">
          {dayChips.map((s) => (
            <ScheduleChip
              key={s.id}
              schedule={s}
              onDragStart={() => {}}
              terminal
              tz={tz}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Main page                                                                 */
/* -------------------------------------------------------------------------- */

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" && window.innerWidth < breakpoint,
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    function onResize() { setIsMobile(window.innerWidth < breakpoint); }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

export default function PublishCalendar() {
  const [anchor, setAnchor] = useState(() => new Date());
  const [state, setState] = useState({ loading: true, error: null });
  const [schedules, setSchedules] = useState([]);
  const [dragOverKey, setDragOverKey] = useState(null);
  const [flash, setFlash] = useState(null);
  const tz = useOrgTimezone();
  const isMobile = useIsMobile(768);

  const load = useCallback(async () => {
    setState({ loading: true, error: null });
    try {
      const from = startOfMonth(anchor);
      const to = endOfMonth(anchor);
      const fromWide = new Date(from); fromWide.setDate(fromWide.getDate() - 7);
      const toWide = new Date(to); toWide.setDate(toWide.getDate() + 7);
      const q = new URLSearchParams({
        from_dt: fromWide.toISOString(),
        to_dt:   toWide.toISOString(),
        limit:   "500",
      });
      const r = await api.get(`/publish-schedules?${q.toString()}`);
      setSchedules(r.data.schedules || []);
      setState({ loading: false, error: null });
    } catch (e) {
      setState({ loading: false, error: formatApiError(e) });
    }
  }, [anchor]);

  useEffect(() => { load(); }, [load]);

  const cells = useMemo(() => buildMonthGrid(anchor), [anchor]);

  // Bucket chips by the day-key *in the org timezone* so a 7 AM ET post
  // on Nov 2 lives under Nov 2 in both EST and EDT.
  const byDay = useMemo(() => {
    const map = {};
    for (const s of schedules) {
      const key = dateKeyInTz(s.scheduled_for, tz) || isoDayKey(parseIsoAsUtc(s.scheduled_for));
      (map[key] = map[key] || []).push(s);
    }
    for (const k in map) {
      map[k].sort((a, b) => parseIsoAsUtc(a.scheduled_for) - parseIsoAsUtc(b.scheduled_for));
    }
    return map;
  }, [schedules, tz]);

  function handleDragStart(e, schedule) {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/json", JSON.stringify({
      id: schedule.id, scheduled_for: schedule.scheduled_for,
    }));
  }
  function handleDragOver(e, key) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverKey !== key) setDragOverKey(key);
  }
  function handleDragLeave() { setDragOverKey(null); }

  async function handleDrop(e, targetDate) {
    e.preventDefault();
    setDragOverKey(null);
    let payload;
    try { payload = JSON.parse(e.dataTransfer.getData("application/json")); }
    catch { return; }
    if (!payload?.id) return;

    // Preserve hour-of-day *in the org timezone* when the user drops
    // onto a new calendar day. Build a datetime-local string whose
    // date comes from the drop target and whose time comes from the
    // dragged chip (both read in the same zone), then convert back to
    // UTC via `parseDatetimeLocalInTz` so DST transitions work.
    const origParts = partsInTz(payload.scheduled_for, tz) || { hour: 9, minute: 0 };
    const z = (n) => String(n).padStart(2, "0");
    const nextLocal = `${targetDate.getFullYear()}-${z(targetDate.getMonth() + 1)}-${z(targetDate.getDate())}T${z(origParts.hour)}:${z(origParts.minute)}`;
    const next = parseDatetimeLocalInTz(nextLocal, tz);
    if (!next || isNaN(next.getTime())) return;

    if (next.getTime() < Date.now() - 60000) {
      if (!window.confirm("This time is in the past. The scheduler will attempt to run it on the next tick. Continue?")) return;
    }
    try {
      await api.patch(`/publish-schedules/${payload.id}`, {
        scheduled_for: next.toISOString(),
      });
      setFlash(`Rescheduled to ${fmtDateTimeTz(next.toISOString(), tz)}.`);
      load();
    } catch (err) {
      setState((s) => ({ ...s, error: formatApiError(err) }));
    }
  }

  const monthLabel = `${MONTHS[anchor.getMonth()]} ${anchor.getFullYear()}`;

  return (
    <Shell title="Publish Calendar">
      <div className="bo-page" data-testid="publish-calendar-page">
        <section className="bo-card" data-testid="publish-calendar-header">
          <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 320px", minWidth: 200 }}>
              <div className="bo-eyebrow">Publish Infrastructure</div>
              <h1 className="bo-serif" style={{ fontSize: 26, margin: "6px 0 0" }}>
                {monthLabel}
              </h1>
              <p style={{ marginTop: 6, color: "var(--bo-muted)", maxWidth: 640, lineHeight: 1.5 }}>
                Drag any pending schedule onto another day to reschedule. The hour of day is preserved; only the date changes. All times shown in <strong data-testid="cal-tz-label">{tz.timezone}{tz.abbr ? ` (${tz.abbr})` : ""}</strong>.
              </p>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <button className="bo-btn bo-btn-ghost" data-testid="cal-prev-month" onClick={() => setAnchor(addMonths(anchor, -1))}>← Prev</button>
              <button className="bo-btn bo-btn-ghost" data-testid="cal-today" onClick={() => setAnchor(new Date())}>Today</button>
              <button className="bo-btn bo-btn-ghost" data-testid="cal-next-month" onClick={() => setAnchor(addMonths(anchor, 1))}>Next →</button>
              <Link to="/brand-os/publish/queue" className="bo-btn bo-btn-ghost" data-testid="cal-open-queue" style={{ textDecoration: "none" }}>
                Queue view
              </Link>
              <Link to="/brand-os/settings" className="bo-btn bo-btn-ghost" data-testid="cal-open-settings" style={{ textDecoration: "none", fontSize: 12 }}>
                Settings
              </Link>
            </div>
          </div>
          {flash && (
            <div className="bo-flash" data-testid="publish-calendar-flash" style={{ marginTop: 12, padding: 10, borderRadius: 8, background: "var(--bo-accent-soft)", color: "var(--bo-ink)", fontSize: 13 }}>
              {flash}
            </div>
          )}
        </section>

        <section style={{ marginTop: 20 }}>
          <ErrorBanner error={state.error} testId="publish-calendar-error" />
          {state.loading ? (
            <LoadingCard label="Loading schedules…" />
          ) : isMobile ? (
            /* ------------- Agenda view (< 768px) ------------- */
            <div className="bo-cal-agenda" data-testid="publish-calendar-agenda">
              {cells
                .filter((d) => d.getMonth() === anchor.getMonth())
                .map((d) => {
                  const key = isoDayKey(d);
                  const dayChips = byDay[key] || [];
                  const today = sameDay(d, new Date());
                  return (
                    <AgendaDay
                      key={key}
                      dayKey={key}
                      dayChips={dayChips}
                      tz={tz}
                      isToday={today}
                      dateObj={d}
                    />
                  );
                })}
            </div>
          ) : (
            /* ------------- Grid view (>= 768px) ------------- */
            <div className="bo-cal-grid-wrap" data-testid="publish-calendar-grid">
              <div className="bo-cal-grid-rows">
                {/* Shared grid template — header and body use the same
                    7-column `grid-template-columns` so SUN–SAT labels
                    line up perfectly with the day cells. */}
                <div className="bo-cal-weekdays" role="row">
                  {DAY_NAMES.map((name) => (
                    <div key={name} className="bo-cal-weekday" role="columnheader">
                      {name}
                    </div>
                  ))}
                </div>
                <div className="bo-cal-grid">
                  {cells.map((d) => {
                    const key = isoDayKey(d);
                    const inMonth = d.getMonth() === anchor.getMonth();
                    const today = sameDay(d, new Date());
                    const dayChips = byDay[key] || [];
                    return (
                      <div
                        key={key}
                        className={`bo-cal-cell ${inMonth ? "" : "bo-cal-cell-out"} ${today ? "bo-cal-cell-today" : ""} ${dragOverKey === key ? "bo-cal-cell-dropzone" : ""}`}
                        data-testid={`cal-cell-${key}`}
                        onDragOver={(e) => handleDragOver(e, key)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, d)}
                      >
                        <div className="bo-cal-cell-header">
                          <span>{d.getDate()}</span>
                          {today && <span className="bo-cal-today-badge">Today</span>}
                        </div>
                        <div className="bo-cal-cell-body">
                          {dayChips.map((s) => (
                            <ScheduleChip
                              key={s.id}
                              schedule={s}
                              onDragStart={handleDragStart}
                              terminal={["executed", "failed", "canceled", "missed"].includes(s.state)}
                              tz={tz}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          {!state.loading && schedules.length === 0 && (
            <div style={{ marginTop: 20 }}>
              <EmptyState
                testId="publish-calendar-empty"
                title="Nothing scheduled this month"
                description="Schedule your next publish and it'll appear here — drag between days to reschedule."
              />
            </div>
          )}
        </section>
      </div>
    </Shell>
  );
}
